import React, { useState, useEffect } from 'react';
import api from '../services/api';

const BankAccountModal = ({ account, isOpen, onClose, bankAccounts, onRefresh }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('90'); // days
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(account?.id || 'all');
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [fundsAmount, setFundsAmount] = useState('');
  const [fundsDescription, setFundsDescription] = useState('');
  const [showRemoveFundsModal, setShowRemoveFundsModal] = useState(false);
  const [removeFundsAmount, setRemoveFundsAmount] = useState('');
  const [removeFundsDescription, setRemoveFundsDescription] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferDescription, setTransferDescription] = useState('');
  const [transferToAccountId, setTransferToAccountId] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const handleDeleteTransaction = async (t) => {
    const type = t.type || 'entry';
    const id = t.id;
    if (!confirm(`Delete this ${type} transaction? This will reverse the bank balance.`)) return;
    setDeletingId(id);
    try {
      await api.deleteTransaction(type, id);
      loadTransactions();
      onRefresh();
    } catch (err) {
      alert('Failed to delete: ' + (err.message || err));
    } finally {
      setDeletingId(null);
    }
  };

  // Update selected account when account prop changes
  useEffect(() => {
    if (account?.id) {
      setSelectedAccountId(account.id);
    }
  }, [account?.id]);

  useEffect(() => {
    if (isOpen && account) {
      loadTransactions();
    }
  }, [isOpen, account, dateRange]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      // Load transactions for selected account or all accounts
      const accountIdToLoad = selectedAccountId === 'all' ? null : selectedAccountId;
      
      // Try reconciliation first, then fallback to accounting entries
      let allTransactions = [];
      
      if (accountIdToLoad) {
        try {
          const reconciliation = await api.getReconciliation(accountIdToLoad, startDate, endDate);
          allTransactions = reconciliation.transactions || [];
        } catch (e) {
          const entries = await api.getAccountingEntries({
            bankAccountId: accountIdToLoad,
            startDate,
            endDate,
            limit: 500
          });
          allTransactions = entries || [];
        }
      } else {
        // Load for all accounts
        const allPromises = bankAccounts.map(async (acc) => {
          try {
            const reconciliation = await api.getReconciliation(acc.id, startDate, endDate);
            return (reconciliation.transactions || []).map(t => ({ ...t, accountName: acc.name, accountId: acc.id }));
          } catch (e) {
            try {
              const entries = await api.getAccountingEntries({
                bankAccountId: acc.id,
                startDate,
                endDate,
                limit: 200
              });
              return (entries || []).map(t => ({ ...t, accountName: acc.name, accountId: acc.id }));
            } catch (e2) {
              return [];
            }
          }
        });
        const results = await Promise.all(allPromises);
        allTransactions = results.flat();
      }
      
      // Sort by date descending
      allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTransactions(allTransactions);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Reload when selected account changes
  useEffect(() => {
    if (isOpen) {
      loadTransactions();
    }
  }, [selectedAccountId, dateRange]);

  const generateMockTransactions = (account, days) => {
    const transactions = [];
    let runningBalance = parseFloat(account.balance);
    
    for (let i = 0; i < Math.min(days, 20); i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      // Generate random transaction
      const isDebit = Math.random() > 0.6;
      const amount = isDebit ? 
        -(Math.random() * 2000 + 100) : 
        (Math.random() * 5000 + 200);
      
      runningBalance -= amount; // Reverse calculation since we're going backwards
      
      transactions.push({
        id: i,
        date: date.toISOString().split('T')[0],
        description: isDebit ? 
          ['Vehicle Purchase', 'Transport Cost', 'Auction Fee', 'Repair Cost'][Math.floor(Math.random() * 4)] :
          'Deposit',
        amount: amount.toFixed(2),
        balance: runningBalance.toFixed(2)
      });
    }
    return transactions;
  };

  const handleAddFunds = async () => {
    if (!fundsAmount || isNaN(fundsAmount)) {
      alert('Please enter a valid amount');
      return;
    }
    try {
      // Create an accounting entry (backend also updates bank balance)
      await api.createAccountingEntry({
        date: new Date().toISOString().split('T')[0],
        entryType: 'credit',
        description: fundsDescription || 'Funds Added',
        amount: parseFloat(fundsAmount),
        bankAccountId: account.id
      });
      
      alert('Funds added successfully!');
      setShowAddFundsModal(false);
      setFundsAmount('');
      setFundsDescription('');
      loadTransactions(); // Refresh transactions
      onRefresh(); // Refresh bank accounts to update balances
    } catch (error) {
      console.error('Failed to add funds:', error);
      alert('Failed to add funds: ' + error.message);
    }
  };

  const handleRemoveFunds = async () => {
    if (!removeFundsAmount || isNaN(removeFundsAmount)) {
      alert('Please enter a valid amount');
      return;
    }
    try {
      // Create accounting entry (also updates bank balance)
      await api.createAccountingEntry({
        date: new Date().toISOString().split('T')[0],
        entryType: 'debit',
        description: removeFundsDescription || 'Funds Withdrawn',
        amount: parseFloat(removeFundsAmount),
        bankAccountId: account.id
      });
      
      alert('Funds removed successfully!');
      setShowRemoveFundsModal(false);
      setRemoveFundsAmount('');
      setRemoveFundsDescription('');
      loadTransactions(); // Refresh transactions
      onRefresh(); // Refresh bank accounts to update balances
    } catch (error) {
      console.error('Failed to remove funds:', error);
      alert('Failed to remove funds: ' + error.message);
    }
  };

  const handleTransferFunds = async () => {
    if (!transferAmount || isNaN(transferAmount) || !transferToAccountId) {
      alert('Please enter a valid amount and select an account to transfer to');
      return;
    }
    try {
      const transferAmountNum = parseFloat(transferAmount);
      const transferDate = new Date().toISOString().split('T')[0];
      const transferDesc = transferDescription || 'Funds Transferred';

      // Debit entry from source account (backend updates balance)
      await api.createAccountingEntry({
        date: transferDate,
        entryType: 'debit',
        description: transferDesc,
        amount: transferAmountNum,
        bankAccountId: account.id
      });

      // Credit entry to destination account (backend updates balance)
      await api.createAccountingEntry({
        date: transferDate,
        entryType: 'credit',
        description: transferDesc,
        amount: transferAmountNum,
        bankAccountId: parseInt(transferToAccountId)
      });

      alert('Funds transferred successfully!');
      setShowTransferModal(false);
      loadTransactions(); // Refresh transactions
      onRefresh(); // Refresh bank accounts to update balances
    } catch (error) {
      console.error('Failed to transfer funds:', error);
      alert('Failed to transfer funds: ' + error.message);
    }
  };

  if (!isOpen) return null;

  // Filter transactions by search term
  const filteredTransactions = transactions.filter(t => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (t.description || '').toLowerCase().includes(search) ||
      (t.vendor_name || '').toLowerCase().includes(search) ||
      (t.stock_number || '').toLowerCase().includes(search) ||
      (t.category || '').toLowerCase().includes(search) ||
      (t.accountName || '').toLowerCase().includes(search) ||
      String(t.amount || '').includes(search) ||
      String(t.amount || '').replace(/[^0-9.]/g, '').includes(search.replace(/[^0-9.]/g, ''))
    );
  });

  // Get current account for display
  const currentAccount = selectedAccountId === 'all' 
    ? null 
    : bankAccounts?.find(a => a.id === selectedAccountId) || account;

  // Calculate totals
  const totalDebits = filteredTransactions.reduce((sum, t) => {
    const amt = parseFloat(t.amount || 0);
    return t.entryType === 'debit' || t.type === 'expense' || t.type === 'bill' || t.type === 'part' ? sum + amt : sum;
  }, 0);
  const totalCredits = filteredTransactions.reduce((sum, t) => {
    const amt = parseFloat(t.amount || 0);
    return ['credit', 'income'].includes(t.entryType) ? sum + amt : sum;
  }, 0);

  // Compute running balance (balance before/after) for single account view - newest first
  const transactionsWithBalance = selectedAccountId === 'all' || !currentAccount ? filteredTransactions : (() => {
    const currentBalance = parseFloat(currentAccount?.balance || 0);
    let runningBalance = currentBalance;
    return filteredTransactions.map(t => {
      const isDebit = t.entryType === 'debit' || t.type === 'expense' || t.type === 'bill' || t.type === 'part';
      const amt = parseFloat(t.amount || 0);
      const balanceAfter = runningBalance;
      const balanceBefore = isDebit ? balanceAfter + amt : balanceAfter - amt;
      runningBalance = balanceBefore;
      return { ...t, balanceBefore, balanceAfter };
    });
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Account Ledger</h2>
            <div className="text-sm text-neutral-500">
              {selectedAccountId === 'all' ? 'All Accounts' : currentAccount?.name}
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
        </div>
        
        <div className="p-4 flex-1 overflow-hidden flex flex-col">
          {/* Account Selector and Balances */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            <button
              onClick={() => setSelectedAccountId('all')}
              className={`p-2 rounded-lg text-left border ${selectedAccountId === 'all' ? 'border-blue-500 bg-blue-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
            >
              <div className="text-xs text-neutral-500">All Accounts</div>
              <div className={`text-sm font-mono font-medium ${bankAccounts?.reduce((sum, a) => sum + parseFloat(a.balance || 0), 0) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                ${bankAccounts?.reduce((sum, a) => sum + parseFloat(a.balance || 0), 0).toLocaleString()}
              </div>
            </button>
            {bankAccounts?.map(acc => (
              <button
                key={acc.id}
                onClick={() => setSelectedAccountId(acc.id)}
                className={`p-2 rounded-lg text-left border ${selectedAccountId === acc.id ? 'border-blue-500 bg-blue-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
              >
                <div className="text-xs text-neutral-500 truncate">{acc.name}</div>
                <div className={`text-sm font-mono font-medium ${parseFloat(acc.balance) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                  {parseFloat(acc.balance) < 0 ? '-' : ''}${Math.abs(parseFloat(acc.balance)).toLocaleString()}
                </div>
              </button>
            ))}
          </div>

          {/* Action Buttons (only for single account) */}
          {currentAccount && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setShowAddFundsModal(true)}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
              >
                + Add Funds
              </button>
              <button
                onClick={() => setShowRemoveFundsModal(true)}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
              >
                - Remove Funds
              </button>
              <button
                onClick={() => setShowTransferModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
              >
                ↔ Transfer
              </button>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
            <select
              value={dateRange}
              onChange={e => setDateRange(e.target.value)}
              className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white"
            >
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last year</option>
            </select>
          </div>

          {/* Summary Row */}
          <div className="flex gap-4 mb-3 text-sm flex-wrap">
            <div className="text-neutral-600">
              <span className="font-medium">{filteredTransactions.length}</span> transactions
            </div>
            <div className="text-red-600">
              Debits: <span className="font-mono font-medium">-${totalDebits.toLocaleString()}</span>
            </div>
            <div className="text-green-600">
              Credits: <span className="font-mono font-medium">+${totalCredits.toLocaleString()}</span>
            </div>
            {currentAccount && (
              <div className="text-neutral-700 font-medium">
                Current Balance: <span className={parseFloat(currentAccount.balance) < 0 ? 'text-red-600' : 'text-green-700'}>${parseFloat(currentAccount.balance || 0).toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Transaction List */}
          <div className="flex-1 overflow-auto border border-neutral-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 sticky top-0">
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                  <th className="px-3 py-2">Date</th>
                  {selectedAccountId === 'all' && <th className="px-3 py-2">Account</th>}
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Details</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  {currentAccount && <th className="px-3 py-2 text-right">Balance Before</th>}
                  {currentAccount && <th className="px-3 py-2 text-right">Balance After</th>}
                  <th className="px-3 py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={selectedAccountId === 'all' ? 7 : 8} className="text-center py-8 text-neutral-500">Loading transactions...</td></tr>
                ) : transactionsWithBalance.length === 0 ? (
                  <tr><td colSpan={selectedAccountId === 'all' ? 7 : 8} className="text-center py-8 text-neutral-500">No transactions found</td></tr>
                ) : (
                  transactionsWithBalance.map((t, idx) => (
                    <tr key={`${t.type}-${t.id}-${idx}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">{t.date?.split('T')[0]}</td>
                      {selectedAccountId === 'all' && (
                        <td className="px-3 py-2">
                          <span className="text-xs bg-neutral-100 px-1.5 py-0.5 rounded">{t.accountName}</span>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          t.type === 'expense' ? 'bg-blue-100 text-blue-700' :
                          t.type === 'bill' ? 'bg-orange-100 text-orange-700' :
                          t.type === 'part' ? 'bg-purple-100 text-purple-700' :
                          ['credit', 'income'].includes(t.entryType) ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {t.type === 'expense' ? 'Expense' : 
                           t.type === 'bill' ? 'Bill' : 
                           t.type === 'part' ? 'Part' : 
                           ['credit', 'income'].includes(t.entryType) ? 'Credit' : 'Debit'}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium max-w-[200px] truncate">{t.description}</td>
                      <td className="px-3 py-2 text-xs text-neutral-500">
                        {t.vendor_name && <span className="mr-2">{t.vendor_name}</span>}
                        {t.stock_number && <span className="font-mono">#{t.stock_number}</span>}
                        {t.category && <span className="ml-2 bg-neutral-100 px-1 rounded">{t.category}</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-medium ${
                        ['credit', 'income'].includes(t.entryType) ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {['credit', 'income'].includes(t.entryType) ? '+' : '-'}${Math.abs(parseFloat(t.amount || 0)).toLocaleString()}
                      </td>
                      {currentAccount && (
                        <>
                          <td className="px-3 py-2 text-right font-mono text-xs text-neutral-600">
                            {t.balanceBefore !== undefined ? `$${t.balanceBefore.toLocaleString()}` : '-'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs font-medium">
                            {t.balanceAfter !== undefined ? `$${t.balanceAfter.toLocaleString()}` : '-'}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleDeleteTransaction(t)}
                          disabled={deletingId === t.id}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 px-1.5 py-0.5 rounded text-xs font-medium disabled:opacity-50"
                          title="Delete transaction"
                        >
                          {deletingId === t.id ? '...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Funds Modal */}
      {showAddFundsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddFundsModal(false)}>
          <div className="bg-white w-full max-w-sm rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <h3 className="text-base font-semibold">Add Funds</h3>
              <button onClick={() => setShowAddFundsModal(false)} className="text-neutral-400 hover:text-neutral-600 text-lg">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={fundsAmount}
                  onChange={e => setFundsAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="e.g., Owner contribution"
                  value={fundsDescription}
                  onChange={e => setFundsDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <button
                onClick={handleAddFunds}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
              >
                Add Funds
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Funds Modal */}
      {showRemoveFundsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowRemoveFundsModal(false)}>
          <div className="bg-white w-full max-w-sm rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <h3 className="text-base font-semibold">Remove Funds</h3>
              <button onClick={() => setShowRemoveFundsModal(false)} className="text-neutral-400 hover:text-neutral-600 text-lg">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm text-neutral-600 bg-neutral-50 p-2 rounded">
                Current balance: <span className={`font-mono font-medium ${parseFloat(account?.balance || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ${parseFloat(account?.balance || 0).toLocaleString()}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Remove</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={removeFundsAmount}
                  onChange={e => setRemoveFundsAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="e.g., Cash withdrawal, Owner draw"
                  value={removeFundsDescription}
                  onChange={e => setRemoveFundsDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
              </div>
              <button
                onClick={handleRemoveFunds}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
              >
                Remove Funds
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Funds Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowTransferModal(false)}>
          <div className="bg-white w-full max-w-sm rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <h3 className="text-base font-semibold">Transfer Funds</h3>
              <button onClick={() => setShowTransferModal(false)} className="text-neutral-400 hover:text-neutral-600 text-lg">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={transferAmount}
                  onChange={e => setTransferAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="e.g., Transfer to savings"
                  value={transferDescription}
                  onChange={e => setTransferDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Account</label>
                <select
                  value={transferToAccountId}
                  onChange={e => setTransferToAccountId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">Select destination account</option>
                  {bankAccounts?.filter(a => a.id !== account.id).map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleTransferFunds}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
              >
                Transfer Funds
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountModal;
